import type { ResolveDistrictResult } from '@citizen-walk/shared-types';
import { httpsCallable, type Functions } from 'firebase/functions';
import type { User } from 'firebase/auth';
import { useEffect, useState } from 'react';
import type { DistrictState } from './DistrictContext';

/**
 * urlToken → ResolveDistrictResult 解決 hook (sprint 2 stage 2)。
 *
 * docs/97 §3 警戒メモ反映:
 *   1. callable 成功直後に `user.getIdToken(true)` で ID トークン強制 refresh
 *      → Custom Claims (`accessibleDistricts`) を Firestore Rules に即時反映
 *   2. localStorage キャッシュ (7 日 TTL) で再訪時の体感速度向上
 *      → オフライン時もキャッシュがあれば表示継続 (旧 DistrictContext.tsx 踏襲)
 *
 * docs/97 §3 残課題:
 *   - 旧 DistrictContext の dataSettings / mapDisplay / aiAnalysis 等の fields は
 *     ResolveDistrictResult 5 fields に最小化 (Phase 5 sprint で段階追加)
 */

const CACHE_KEY_PREFIX = 'district-cache:';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CacheEntry {
  district: ResolveDistrictResult;
  cachedAt: number;
}

function readCache(urlToken: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY_PREFIX + urlToken);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (typeof parsed.cachedAt !== 'number' || !parsed.district) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(urlToken: string, district: ResolveDistrictResult): void {
  try {
    const entry: CacheEntry = { district, cachedAt: Date.now() };
    localStorage.setItem(CACHE_KEY_PREFIX + urlToken, JSON.stringify(entry));
  } catch {
    /* quota / private mode は無視 */
  }
}

/**
 * 地区解決キャッシュを明示的に失効させる (地区「強い停止」Phase 1)。
 *
 * 主用途: 関係者がアカウントログインした直後 (AccountLoginPage)。停止中は一般ユーザー向けに
 * `suspended:true` の最小返却がキャッシュされているため、ログイン後の再解決でそれを使わず
 * 完全返却を取り直させる。
 */
export function clearDistrictCache(urlToken: string): void {
  try {
    localStorage.removeItem(CACHE_KEY_PREFIX + urlToken);
  } catch {
    /* private mode 等は無視 */
  }
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.cachedAt < CACHE_TTL_MS;
}

export interface UseDistrictResolverArgs {
  urlToken: string | undefined;
  user: User | null;
  functions: Functions;
}

export function useDistrictResolver({
  urlToken,
  user,
  functions,
}: UseDistrictResolverArgs): DistrictState {
  const [state, setState] = useState<DistrictState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    function applyState(next: DistrictState) {
      if (cancelled) return;
      setState(next);
    }

    if (!urlToken || !user) {
      applyState({ status: 'idle' });
      return;
    }

    // docs/13 §3-2: urlToken は 32 文字以上。client 側でも事前検証し
    // 不要 RTT を削る (functions 側でも再検証)。
    if (urlToken.length < 32) {
      applyState({ status: 'error', error: new Error('invalid-argument:urlToken-too-short') });
      return;
    }

    const cache = readCache(urlToken);
    // 地区「強い停止」Phase 1: suspended==true の最小返却はキャッシュとして使わない。
    // 停止中は「最新の停止状態」と「ログイン後の完全返却」を毎回サーバ確認する必要があるため、
    // suspended エントリは即時表示にも offline フォールバックにも使わず常に再 fetch させる。
    // (非停止地区・従来エントリの挙動は不変。後方互換最優先。)
    const cacheUsable = cache !== null && isFresh(cache) && cache.district.suspended !== true;

    if (cacheUsable) {
      applyState({ status: 'ready', district: cache.district, source: 'cache' });
    } else {
      applyState({ status: 'loading' });
    }

    (async () => {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

      if (!online) {
        if (!cacheUsable) {
          applyState({ status: 'error', error: new Error('offline-no-cache') });
        }
        return;
      }

      try {
        const fn = httpsCallable<{ urlToken: string }, ResolveDistrictResult>(
          functions,
          'resolveDistrictByToken',
        );
        const res = await fn({ urlToken });
        if (cancelled) return;
        const fresh = res.data;
        // 警戒メモ #2: Custom Claims 反映のため強制 refresh
        await user.getIdToken(true);
        writeCache(urlToken, fresh);
        applyState({ status: 'ready', district: fresh, source: 'remote' });
      } catch (e) {
        if (cacheUsable) {
          // キャッシュがあれば error 化せず継続 (旧踏襲)
          console.warn('resolveDistrictByToken failed (using cache)', e);
          return;
        }
        const err = e instanceof Error ? e : new Error(String(e));
        applyState({ status: 'error', error: err });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [urlToken, user, functions]);

  return state;
}
